import React, { useState, useEffect } from 'react';
import { User } from '@/types/api';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { updateTimezoneAsync, selectUser } from '@/store/slices/authSlice';
import timezoneService from '@/services/timezoneService';
import { apiClient } from '@/services/apiClient';

interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

const TimezoneSelector: React.FC = () => {
  const { t } = useTranslation(['forms', 'common']);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser) as User | null;
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || 'UTC');
  const [availableTimezones, setAvailableTimezones] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Common timezones for quick selection
  const commonTimezones: TimezoneOption[] = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00' },
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', offset: '+01:00' },
    { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+00:00' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: '+01:00' },
    { value: 'America/New_York', label: 'New York (EST/EDT)', offset: '-05:00' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', offset: '-08:00' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+09:00' },
  ];

  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        const response = await apiClient.get<{ timezones: Record<string, string[]> }>('/auth/timezones');
        setAvailableTimezones(response.timezones);
      } catch (error) {
        console.error('Failed to fetch timezones', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimezones();
  }, []);

  const handleTimezoneChange = async (timezone: string) => {
    setSelectedTimezone(timezone);
    await dispatch(updateTimezoneAsync(timezone));
  };

  const filteredTimezones = Object.entries(availableTimezones).reduce((acc, [region, tzs]) => {
    const filtered = tzs.filter(tz => 
      tz.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[region] = filtered;
    }
    return acc;
  }, {} as Record<string, string[]>);

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('forms:timezonePreference')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('forms:scheduleTimesDisplayInfo')}
      </Typography>
      
      <TextField
        fullWidth
        size="small"
        placeholder={t('forms:searchTimezonesPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <FormControl fullWidth>
        <InputLabel id="timezone-select-label">{t('forms:timezone')}</InputLabel>
        <Select
          labelId="timezone-select-label"
          value={selectedTimezone}
          label={t('forms:timezone')}
          onChange={(e) => handleTimezoneChange(e.target.value)}
          MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
        >
          <ListSubheader>{t('forms:commonTimezones')}</ListSubheader>
          {commonTimezones.map((tz) => (
            <MenuItem key={`common-${tz.value}`} value={tz.value}>
              {tz.label}
            </MenuItem>
          ))}
          
          {Object.entries(filteredTimezones).map(([region, tzs]) => [
            <ListSubheader key={`header-${region}`}>{region}</ListSubheader>,
            ...tzs.map(tz => (
              <MenuItem key={`item-${tz}`} value={tz}>
                {tz.split('/').pop()?.replace('_', ' ')}
              </MenuItem>
            ))
          ])}
        </Select>
      </FormControl>
      
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'primary.main' }}>
        {t('forms:currentTimeInTimezone', {
          timezone: selectedTimezone,
          time: timezoneService.formatDateTimeLocal(new Date().toISOString(), 'p (zzzz)')
        })}
      </Typography>
    </Box>
  );
};

export default TimezoneSelector;
